import L from 'leaflet';

/**
 * Direction arrows along a route.
 *
 * A line on a map says where a car goes but not which way along it, and that is
 * exactly what becomes ambiguous once a route has a stop in the middle: the two
 * halves look alike and there is nothing to say which comes first. Arrows resolve
 * it at a glance, and the shading of each leg does the rest.
 *
 * They are placed by distance on screen rather than by geometry index, so they
 * stay evenly spaced whatever the zoom, and rebuilt whenever the view settles for
 * the same reason. Only the part of the line currently in view is decorated.
 */

const SPACING_PX = 115;
/** Keep clear of the pins at either end, which already say start and finish. */
const END_MARGIN_PX = 46;
const MAX_PER_ROUTE = 14;
/**
 * Look this far along the line, in pixels, to work out which way an arrow points.
 *
 * A single geometry step is often a pixel or two, and at that scale the direction
 * snaps to the screen grid: every arrow ends up at a multiple of 45 degrees, and
 * some point backwards on a bend. Averaging over a span of the line gives the
 * heading a driver would actually recognise.
 */
const HEADING_SPAN_PX = 16;

/** Heading at a point, measured across a span of the line rather than one step. */
function headingAt(screen, index) {
  let back = index;
  let dist = 0;
  while (back > 0 && dist < HEADING_SPAN_PX) {
    dist += Math.hypot(screen[back].x - screen[back - 1].x, screen[back].y - screen[back - 1].y);
    back -= 1;
  }
  let fwd = index;
  dist = 0;
  while (fwd < screen.length - 1 && dist < HEADING_SPAN_PX) {
    dist += Math.hypot(screen[fwd + 1].x - screen[fwd].x, screen[fwd + 1].y - screen[fwd].y);
    fwd += 1;
  }
  const dx = screen[fwd].x - screen[back].x;
  const dy = screen[fwd].y - screen[back].y;
  if (Math.hypot(dx, dy) < 1) return null;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

const arrowIcon = (color, angle) =>
  L.divIcon({
    className: 'leaflet-div-icon-plain route-arrow-icon',
    html:
      `<svg width="15" height="15" viewBox="0 0 12 12" style="transform:rotate(${angle.toFixed(1)}deg)">` +
      `<path d="M2.2 1.4 L10 6 L2.2 10.6 Z" fill="${color}" stroke="rgba(255,255,255,.95)" ` +
      `stroke-width="1.4" stroke-linejoin="round"/></svg>`,
    iconSize: [15, 15],
    iconAnchor: [7.5, 7.5],
  });

export function attachRouteArrows(map, routes) {
  let markers = [];

  const clear = () => {
    for (const m of markers) m.remove();
    markers = [];
  };

  const render = () => {
    clear();
    if (!routes?.length) return;
    const bounds = map.getBounds().pad(0.08);

    for (const route of routes) {
      const points = route.points;
      if (!points || points.length < 2) continue;

      // Total on-screen length first, so the end margins can be honoured.
      const screen = points.map((p) => map.latLngToContainerPoint(p));
      let total = 0;
      for (let i = 1; i < screen.length; i += 1) {
        total += Math.hypot(screen[i].x - screen[i - 1].x, screen[i].y - screen[i - 1].y);
      }
      if (total < END_MARGIN_PX * 2 + SPACING_PX) continue; // too short to decorate

      let travelled = 0;
      let sinceLast = 0;
      let placed = 0;

      for (let i = 1; i < screen.length && placed < MAX_PER_ROUTE; i += 1) {
        const dx = screen[i].x - screen[i - 1].x;
        const dy = screen[i].y - screen[i - 1].y;
        const seg = Math.hypot(dx, dy);
        travelled += seg;
        sinceLast += seg;

        if (sinceLast < SPACING_PX) continue;
        if (travelled < END_MARGIN_PX || travelled > total - END_MARGIN_PX) continue;
        if (!bounds.contains(points[i])) {
          sinceLast = 0;
          continue;
        }
        const heading = headingAt(screen, i);
        if (heading === null) continue;

        sinceLast = 0;
        placed += 1;
        markers.push(
          L.marker(points[i], {
            icon: arrowIcon(route.color, heading),
            interactive: false,
            keyboard: false,
            zIndexOffset: 200,
          }).addTo(map),
        );
      }
    }
  };

  render();
  map.on('zoomend moveend', render);

  return () => {
    map.off('zoomend moveend', render);
    clear();
  };
}
